import { Component, NgZone, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective, ObjectUtils } from '@egym/ngx-adm-int';
import { AddFilterDialogComponent } from 'src/app/components/search-filter/filter/add-filter-dialog/add-filter-dialog.component';
import { AddSearchFilterDialogComponent } from 'src/app/components/search-filter/search-filter/add-search-filter-dialog/add-search-filter-dialog.component';
import { Activity } from 'src/app/model/options/activity';
import { Service } from 'src/app/model/options/service';
import { Filter } from 'src/app/model/search-filters/filter';
import { SearchFilter } from 'src/app/model/search-filters/search-filter';
import { SearchFilterUtils } from 'src/app/model/search-filters/search-filter-utils';
import { StudioType } from 'src/app/model/studio-type';
import { FilterService } from 'src/app/service/filter.service';
import { GymFinderService } from 'src/app/service/gymfinder.service';
import { NetworkService } from 'src/app/service/network.service';
import { SearchFilterService } from 'src/app/service/search-filter.service';

@Component({
  selector: 'gymfinder-search-filters',
  templateUrl: './search-filters.component.html',
  styleUrls: ['./search-filters.component.scss'],
  standalone: false,
})
export class SearchFiltersComponent
  extends BaseDirective<Filter[]>
  implements OnInit
{
  initialSearchFilters: SearchFilter[] = [];

  searchFilters: SearchFilter[] = [];

  initialFilters: Filter[] = [];

  filters: Filter[] = [];

  studioTypes: StudioType[];

  activities: Activity[];

  services: Service[];

  gymEquipments: string[];

  equipmentsToBring: string[];

  gymInfos: string[];

  gymTypes: string[];

  networks: string[];

  geniusFeatures: string[];

  expandedFilterPanel?: number = undefined;

  expandedSearchFilterPanel?: number = undefined;

  searchFiltersLoading = false;

  filtersLoading = false;

  savingSearchFilters: boolean[] = [];

  savingFilters: boolean[] = [];

  searchFilterModified: boolean[] = [];

  filterModified: boolean[] = [];

  constructor(
    snackBar: MatSnackBar,
    private gymFinderService: GymFinderService,
    private filterService: FilterService,
    private searchFilterService: SearchFilterService,
    private networkService: NetworkService,
    private dialog: MatDialog,
    private ngZone: NgZone
  ) {
    super(snackBar);
  }

  ngOnInit(): void {
    this.searchFiltersLoading = true;
    this.filtersLoading = true;

    this.searchFilterService.getAll().subscribe({
      next: next => {
        this.searchFiltersLoading = false;
        this.searchFilters = next;
        this.initialSearchFilters = ObjectUtils.deepCopy(next);
      },
      error: e => {
        this.searchFiltersLoading = false;
        this.openSnackBar(
          'Error while fetching the search filters: ' + e.message
        );
      },
    });

    this.filterService.getAllFilters().subscribe({
      next: next => {
        this.filtersLoading = false;
        this.filters = next;
        this.initialFilters = ObjectUtils.deepCopy(next);
        this.searchFilters = ObjectUtils.deepCopy(this.searchFilters);
      },
      error: e => {
        this.filtersLoading = false;
        this.openSnackBar('Error while fetching the filters: ' + e.message);
      },
    });

    this.gymFinderService.getStudioTypes().subscribe({
      next: next => (this.studioTypes = next),
      error: e =>
        this.openSnackBar(
          'Error while fetching the studio types: ' + e.message
        ),
    });

    this.networkService.network$.subscribe(network => {
      this.gymFinderService.getOptions(network).subscribe({
        next: next => {
          const {
            activities,
            services,
            equipmentsToBring,
            gymEquipments,
            gymInfos,
            gymTypes,
            networks,
            geniusFeatures,
          } = next;
          this.activities = activities;
          this.services = services;
          this.equipmentsToBring = equipmentsToBring;
          this.gymEquipments = gymEquipments;
          this.gymInfos = gymInfos;
          this.gymTypes = gymTypes;
          this.networks = networks;
          this.geniusFeatures = geniusFeatures;
        },
        error: e =>
          this.openSnackBar('Error while fetching the options: ' + e.message),
      });
    });
  }

  public getLogicalExpression(filter: Filter): string {
    return SearchFilterUtils.toLogicString(filter);
  }

  public onSearchFilterChange(newSearchFilter: SearchFilter, index: number) {
    this.searchFilters[index] = newSearchFilter;
    this.searchFilterModified[index] = true;
  }

  public onFilterChange(newFilter: Filter, index: number) {
    this.filters[index] = newFilter;
    this.filterModified[index] = true;
  }

  public onSearchFilterPanelOpened(panelIndex: number) {
    this.expandedSearchFilterPanel = panelIndex;
  }

  public onSearchFilterPanelClosed(panelIndex: number) {
    if (this.expandedSearchFilterPanel === panelIndex) {
      this.expandedSearchFilterPanel = undefined;
    }
  }

  public onFilterPanelOpened(panelIndex: number) {
    this.expandedFilterPanel = panelIndex;
  }

  public onFilterPanelClosed(panelIndex: number) {
    if (this.expandedFilterPanel === panelIndex) {
      this.expandedFilterPanel = undefined;
    }
  }

  public saveSearchFilter(searchFilter: SearchFilter, index: number) {
    if (searchFilter && searchFilter.id) {
      this.savingSearchFilters[index] = true;
      this.searchFilterService.update(searchFilter.id, searchFilter).subscribe({
        next: n => {
          this.searchFilters.splice(index, 1, n);
          this.initialSearchFilters.splice(index, 1, ObjectUtils.deepCopy(n));
          this.searchFilterModified[index] = false;
          this.savingSearchFilters[index] = false;
          this.openSnackBar('The search filter was correctly updated!');
        },
        error: e => {
          this.savingSearchFilters[index] = false;
          this.openSnackBar(
            'There was an error updating the search filter: ' + e.message,
            undefined,
            5000
          );
        },
      });
    }
  }

  public saveFilter(filter: Filter, index: number) {
    if (filter && filter.id) {
      this.savingFilters[index] = true;
      this.filterService.updateFilter(filter.id, filter).subscribe({
        next: n => {
          this.filters.splice(index, 1, n);
          this.initialFilters.splice(index, 1, ObjectUtils.deepCopy(n));
          this.filterModified[index] = false;
          this.savingFilters[index] = false;
          this.updateSearchFilterFilters();
          this.openSnackBar('The filter was correctly updated!');
        },
        error: e => {
          this.savingFilters[index] = false;
          this.openSnackBar(
            'There was an error updating the filter: ' + e.message,
            undefined,
            5000
          );
        },
      });
    }
  }

  private updateSearchFilterFilters() {
    if (this.searchFilters) {
      this.searchFilters.forEach(sf => {
        if (sf.items) {
          sf.items.forEach(i => {
            if (i.filters) {
              const itemFilteIds = i.filters.map(f => f.id);
              i.filters = this.filters.filter(filter =>
                itemFilteIds.find(id => id === filter.id)
              );
            }
          });
        }
      });

      this.searchFilters = ObjectUtils.deepCopy(this.searchFilters);
    }
  }

  public cancelSearchFilterEdit(index: number) {
    this.searchFilterModified[index] = false;
    if (this.initialSearchFilters.length > index) {
      this.searchFilters[index] = ObjectUtils.deepCopy(
        this.initialSearchFilters[index]
      );
    } else {
      this.searchFilters.splice(index, 1);
    }
  }

  public cancelFilterEdit(index: number) {
    this.filterModified[index] = false;
    if (this.initialFilters.length > index) {
      this.filters[index] = ObjectUtils.deepCopy(this.initialFilters[index]);
    } else {
      this.filters.splice(index, 1);
    }
  }

  public onAddNewSearchFilter() {
    this.ngZone.run(() => {
      const dialogRef = this.dialog.open(AddSearchFilterDialogComponent, {
        data: {},
      });
      dialogRef.afterClosed().subscribe({
        next: n => {
          this.createSearchFilter(n);
        },
      });
    });
  }

  private createSearchFilter(newSearchFilter: SearchFilter) {
    if (newSearchFilter) {
      this.searchFiltersLoading = true;
      this.searchFilterService.create(newSearchFilter).subscribe({
        next: n => {
          this.searchFiltersLoading = false;
          this.searchFilters.push(n);
          this.initialSearchFilters.push(ObjectUtils.deepCopy(n));
          this.openSnackBar('The search filter was correctly created!');
        },
        error: e => {
          this.searchFiltersLoading = false;
          this.openSnackBar(
            'There was an error creating the search filter: ' + e.message,
            undefined,
            5000
          );
        },
      });
    }
  }

  public onAddNewFilter() {
    this.ngZone.run(() => {
      const dialogRef = this.dialog.open(AddFilterDialogComponent, {
        data: {
          studioTypes: this.studioTypes,
          activities: this.activities,
          services: this.services,
          gymEquipments: this.gymEquipments,
          gymInfos: this.gymInfos,
          gymTypes: this.gymTypes,
          networks: this.networks,
          equipmentsToBring: this.equipmentsToBring,
          geniusFeatures: this.geniusFeatures,
        },
      });
      dialogRef.afterClosed().subscribe({
        next: n => {
          this.createFilter(n);
        },
      });
    });
  }

  private createFilter(newFilter: Filter) {
    if (newFilter) {
      this.filtersLoading = true;
      this.filterService.createFilter(newFilter).subscribe({
        next: n => {
          this.filtersLoading = false;
          this.filters.push(n);
          this.initialFilters.push(ObjectUtils.deepCopy(n));
          this.searchFilters = ObjectUtils.deepCopy(this.searchFilters);
          this.openSnackBar('The filter was correctly created!');
        },
        error: e => {
          this.filtersLoading = false;
          this.openSnackBar(
            'There was an error creating the filter: ' + e.message,
            undefined,
            5000
          );
        },
      });
    }
  }
}
